import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from "react";

export interface VehicleFilter {
  year: string;
  make: string;
  model: string;
  /** Optional: catalog generation ID for precision matching */
  generationId?: number;
}

interface VehicleContextType {
  vehicle: VehicleFilter;
  setVehicle: (v: Partial<VehicleFilter>) => void;
  clearVehicle: () => void;
  hasVehicle: boolean;
}

const EMPTY: VehicleFilter = {
  year: "",
  make: "",
  model: "",
  generationId: undefined,
};

const VehicleContext = createContext<VehicleContextType>({
  vehicle: EMPTY,
  setVehicle: () => {},
  clearVehicle: () => {},
  hasVehicle: false,
});

export function VehicleProvider({ children }: { children: ReactNode }) {
  const [vehicle, setVehicleState] = useState<VehicleFilter>(() => {
    try {
      const stored = localStorage.getItem("voom_vehicle");
      return stored ? JSON.parse(stored) : EMPTY;
    } catch {
      return EMPTY;
    }
  });

  const setVehicle = useCallback((v: Partial<VehicleFilter>) => {
    setVehicleState(prev => {
      const next = { ...prev, ...v };
      if (v.make !== undefined && v.make !== prev.make) {
        next.model = "";
      }
      localStorage.setItem("voom_vehicle", JSON.stringify(next));
      return next;
    });
  }, []);

  const clearVehicle = useCallback(() => {
    setVehicleState(EMPTY);
    localStorage.removeItem("voom_vehicle");
  }, []);

  const hasVehicle = !!(vehicle.year || vehicle.make || vehicle.model);

  return (
    <VehicleContext.Provider
      value={{ vehicle, setVehicle, clearVehicle, hasVehicle }}
    >
      {children}
    </VehicleContext.Provider>
  );
}

export function useVehicle() {
  return useContext(VehicleContext);
}

/**
 * Check if a product fits the buyer's saved vehicle.
 * Works with both legacy flat fields AND new structured fitments.
 * If the product has fitments[], we check those first for precision.
 * Falls back to flat vehicleMake/vehicleModel/yearFrom/yearTo.
 */
export function vehicleFitsProduct(
  vehicle: VehicleFilter,
  product: {
    vehicleMake?: string | null;
    vehicleModel?: string | null;
    yearFrom?: number | null;
    yearTo?: number | null;
    fitments?: Array<{
      makeName?: string | null;
      modelName?: string | null;
      yearStart?: number | null;
      yearEnd?: number | null;
      generationId?: number | null;
    }> | null;
  }
): boolean {
  if (!vehicle.make) return false;

  // ── Check structured fitments first (moat) ──
  if (product.fitments && product.fitments.length > 0) {
    const yr = parseInt(vehicle.year);
    return product.fitments.some(f => {
      if (!f.makeName) return false;
      if (f.makeName.toLowerCase() !== vehicle.make.toLowerCase()) return false;
      if (vehicle.model && f.modelName) {
        const a = f.modelName.toLowerCase();
        const b = vehicle.model.toLowerCase();
        if (!a.includes(b) && !b.includes(a)) return false;
      }
      if (vehicle.generationId && f.generationId) {
        if (vehicle.generationId !== f.generationId) return false;
      }
      if (!isNaN(yr)) {
        if (f.yearEnd && yr > f.yearEnd) return false;
        if (f.yearStart && yr < f.yearStart) return false;
      }
      return true;
    });
  }

  // ── Fallback: legacy flat fields ──
  if (!product.vehicleMake) return false;
  const makeMatch =
    product.vehicleMake.toLowerCase() === vehicle.make.toLowerCase();
  if (!makeMatch) return false;
  if (vehicle.model && product.vehicleModel) {
    const a = product.vehicleModel.toLowerCase();
    const b = vehicle.model.toLowerCase();
    if (!a.includes(b) && !b.includes(a)) return false;
  }
  const yr = parseInt(vehicle.year);
  if (!isNaN(yr)) {
    if (product.yearTo && yr > product.yearTo) return false;
    if (product.yearFrom && yr < product.yearFrom) return false;
  }
  return true;
}
